import React, { useState } from 'react';
import axios from 'axios';
import { useForm } from 'antd/lib/form/Form';
import { Col, Row } from 'antd/lib/grid';
import { Unsubscribe } from '@yandex/smart-captcha/typings/api';
import { Form, FORM_ITEM_RULES, PHONE_MASK } from '@perxis-auto/forms';
import { Button } from '@perxis/perxis-auto-ui-kit/lib/components/Button';
import { Checkbox } from '@perxis/perxis-auto-ui-kit/lib/components/Checkbox';
import { Input } from '@perxis/perxis-auto-ui-kit/lib/components/Input';
import { InputMask } from '@perxis/perxis-auto-ui-kit/lib/components/InputMask';
import { Select } from '@perxis/perxis-auto-ui-kit/lib/components/Select';
import { FormItem } from '@perxis/perxis-auto-ui-kit/lib/components/FormItem';
import { Typography } from '@perxis/perxis-auto-ui-kit/lib/components/Typography';

import { sendBookingRequest, TBookingRequestResponse } from '@/api/payService';
import { ICarItem } from '@/types/car';
import config from '@/utils/config';
import { formatPhone } from '@/utils/common';
import { CUSTOM_RULES } from '@/utils/validateRules';
import formatCurrency from '@/utils/formatCurrency';
import { CAPTCHA_ERROR_MESSAGES, STATUS_MESSAGES } from '@/constants/common';
import { FORM_EVENTS } from '@/constants/events';
import { POLICY_PERSONAL_URL, LOYALTY_PROGRAM_URL } from '@/constants/links';
import useDealershipDataFetch, {
  TDealership,
} from '@/hooks/fetch/useDealershipDataFetch';
import { EVENTS_CHANNEL } from '@/apps/pre-order-form/constants/events';
import { BENEFITS } from '@/apps/pre-order-form/constants/benefits';
import { TClientData, TBookingAgreementData } from '@/apps/pre-order-form/types';
import Captcha from '@/ui-components/Captcha';
import ConfirmationStep from '../ConfirmationStep';
import FormLabel from './FormLabel';

import style from './style.module.scss';

type PreOrderFormProps = {
  car: ICarItem;
};

export const PreOrderFormFields: Record<keyof PreOrderFormValues, string> = {
  first_name: 'first_name',
  last_name: 'last_name',
  patronymic: 'patronymic',
  phone: 'phone',
  email: 'email',
  region: 'region',
  dealership: 'dealership',
  dealership_address: 'dealership_address',
  dealership_id: 'dealership_id',
  benefit: 'benefit',
  captcha_token: 'captcha_token',
  confirm_personal_data_processing_policy: 'confirm_personal_data_processing_policy',
  confirm_loyalty_program: 'confirm_loyalty_program',
  promo: 'promo',
};

export type PreOrderFormValues = Omit<TClientData, 'dealership'> & {
  dealership: TDealership['id'] | null;
  captcha_token: string;
};

const initialValues: PreOrderFormValues = {
  first_name: '',
  last_name: '',
  patronymic: '',
  phone: '',
  email: '',
  captcha_token: '',
  dealership: null,
  dealership_address: null,
  dealership_id: null,
  benefit: null,
  region: null,
  confirm_personal_data_processing_policy: true,
};

const PreOrderForm: React.VFC<PreOrderFormProps> = ({ car }: PreOrderFormProps) => {
  const [form] = useForm<PreOrderFormValues>();

  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [sendedCaptcha, setSendedCaptcha] = useState('');
  const [preOrderData, setPreOrderData] = useState<TBookingRequestResponse>();
  const [bookingAgreementData, setBookingAgreementData] =
    useState<TBookingAgreementData>();
  const [formFieldValues, setFormFieldValues] = useState<TClientData>();

  const {
    data: { cities, dealerships },
    getDealershipsByCity,
    loading: dealershipsLoading,
  } = useDealershipDataFetch(
    car?.data?.logisticStatus?.id || '',
    car?.data.brand?.displayName,
  );

  const model = car?.data?.model?.displayName?.toLowerCase();
  const brand = car?.data?.brand?.displayName?.toLowerCase();

  const isC5Form = model === 'C5'.toLowerCase();
  const isS5Form = model === 'S5'.toLowerCase();
  const isS5GTForm = model === 'S5 GT'.toLowerCase();
  const isJaecooForm = brand === 'Jaecoo'.toLowerCase();
  const isOmodaForm = brand === 'Omoda'.toLowerCase();

  const formInitialValues = {
    ...initialValues,
    benefit: isOmodaForm ? 'gift' : isJaecooForm ? 'discount' : null,
    ...config.get('initialValues'),
  };

  const onFinish = async ({
    [PreOrderFormFields.captcha_token]: captchaToken,
    ...values
  }: any) => {
    let resolvedCaptchaToken = captchaToken;
    if (!resolvedCaptchaToken || sendedCaptcha === resolvedCaptchaToken) {
      setCaptchaVisible(true);
      let unsubscribeSuccess: Unsubscribe = () => {};
      let unsubscribeHidden: Unsubscribe = () => {};
      resolvedCaptchaToken = await new Promise((resolve, reject) => {
        // TODO: Заменить widget id
        unsubscribeSuccess = window.smartCaptcha.subscribe(
          0,
          'success',
          (token: string) => {
            resolve(token);
          },
        );
        unsubscribeHidden = window.smartCaptcha.subscribe(0, 'challenge-hidden', () => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ captchaError: CAPTCHA_ERROR_MESSAGES });
        });
      }).finally(() => {
        unsubscribeSuccess();
        unsubscribeHidden();
        setCaptchaVisible(false);
      });
    }
    const dealership = dealerships.find(
      d => d.id === values?.[PreOrderFormFields.dealership],
    );
    const resolvedValues = {
      ...values,
      [PreOrderFormFields.dealership]: dealership?.name,
      [PreOrderFormFields.dealership_address]: dealership?.address,
      [PreOrderFormFields.dealership_id]: dealership?.id,
    };
    const formattedPhone = formatPhone(resolvedValues?.phone);

    setSendedCaptcha(resolvedCaptchaToken);
    const { data: preOrderResponse } = await sendBookingRequest({
      ...resolvedValues,
      [PreOrderFormFields.captcha_token]: resolvedCaptchaToken,
      [PreOrderFormFields.phone]: formattedPhone,
      session_id: config.get('sessionId'),
      vehicle_id: car.id,
    });

    const { data: html } = await axios.get<string>(
      preOrderResponse.booking_request_html_url,
    );
    setPreOrderData(preOrderResponse);
    setBookingAgreementData({
      html,
    });
    setFormFieldValues(resolvedValues);
  };

  const getErrorMessage = (e: any) => {
    if (e?.response?.data?.detail?.at(0)?.loc?.at(1) === 'promo') {
      return e?.response?.data?.detail?.at(0)?.msg;
    }
    if (e?.response?.data?.detail) {
      return e?.response?.data?.detail;
    }

    return e?.captchaError ?? STATUS_MESSAGES.error;
  };

  const [promoVisible, setPromoVisible] = useState(false);

  const showPromoInput = () => {
    setPromoVisible(true);
  };

  return (
    <Form<PreOrderFormValues>
      form={form}
      formEvents={FORM_EVENTS}
      formChannel={EVENTS_CHANNEL}
      initialValues={formInitialValues}
      onFinish={onFinish}
      className={style.formWrapper}
      finalStep={() => (
        <ConfirmationStep
          data={preOrderData}
          carData={car}
          clientData={formFieldValues}
          bookingAgreementData={bookingAgreementData}
        />
      )}
      successCallback={config.get('successCallback')}
      errorCallback={config.get('errorCallback')}
      formatValues={config.get('formatValues')}
      getErrorMessage={getErrorMessage}
      defaultMessages={STATUS_MESSAGES}
    >
      {({ values, changeFieldValue, isSubmitting, status }) => {
        const { benefit, region, dealership } = values || {};
        const showErrorMessage = status?.type === 'error' && !!status.message;

        const filteredDealerships = getDealershipsByCity(region);
        const currentBenefit = BENEFITS.find(b => b.id === benefit);

        return (
          <>
            <Typography.Title className={style.title} level={3} classLevel={1}>
              Сделайте предзаказ
            </Typography.Title>
            <div className={style.descriptionWrapper}>
              <Typography.Text className={style.description}>
                <FormLabel
                  model={car?.data?.model?.displayName}
                  brand={car?.data?.brand?.displayName}
                />
              </Typography.Text>
            </div>
            <Row
              gutter={[
                { sm: 40, xs: 0 },
                { sm: 30, xs: 20 },
              ]}
            >
              <Col sm={12} xs={24}>
                <FormItem
                  label="Имя"
                  name={PreOrderFormFields.first_name}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.minLength(2),
                    FORM_ITEM_RULES.maxLength(25),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Фамилия"
                  name={PreOrderFormFields.last_name}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.minLength(2),
                    FORM_ITEM_RULES.maxLength(25),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Отчество"
                  name={PreOrderFormFields.patronymic}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.minLength(2),
                    FORM_ITEM_RULES.maxLength(25),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Город"
                  name={PreOrderFormFields.region}
                  rules={[FORM_ITEM_RULES.required]}
                  loading={dealershipsLoading}
                >
                  <Select
                    disabled={!cities.length || dealershipsLoading}
                    loading={dealershipsLoading}
                    onChange={city => {
                      if (dealership) changeFieldValue({ dealership: null });

                      if (city) {
                        const filteredDealers = getDealershipsByCity(city as string);
                        filteredDealers.length === 1 &&
                          changeFieldValue({
                            dealership: filteredDealers[0].id,
                          });
                      }
                    }}
                  >
                    {cities.map(city => (
                      <Select.Option key={city} value={city}>
                        {city}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              <Col span={24}>
                <FormItem
                  label="Дилерский центр"
                  name={PreOrderFormFields.dealership}
                  rules={[FORM_ITEM_RULES.required]}
                  loading={dealershipsLoading}
                >
                  <Select
                    disabled={!region || dealershipsLoading}
                    loading={dealershipsLoading}
                  >
                    {filteredDealerships.map(({ id, fullName }) => (
                      <Select.Option key={id} value={id}>
                        {fullName}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Телефон"
                  name={PreOrderFormFields.phone}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.phone,
                    FORM_ITEM_RULES.maxLength(20),
                  ]}
                >
                  <InputMask
                    type="tel"
                    mask={PHONE_MASK}
                    maskOptions={{
                      lazy: true,
                      placeholderChar: 'X',
                    }}
                  />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="E-mail"
                  name={PreOrderFormFields.email}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.email,
                    FORM_ITEM_RULES.maxLength(255),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              {/*{!(isS5Form || isS5GTForm || isJaecooForm) && (*/}
              {/*  <Col span={24}>*/}
              {/*    <FormItem*/}
              {/*      label="Выбор выгоды за предзаказ"*/}
              {/*      name={PreOrderFormFields.benefit}*/}
              {/*      rules={[FORM_ITEM_RULES.required]}*/}
              {/*    >*/}
              {/*      <Select>*/}
              {/*        {BENEFITS.map(({ id, name }) => (*/}
              {/*          <Select.Option key={id} value={id}>*/}
              {/*            {name}*/}
              {/*          </Select.Option>*/}
              {/*        ))}*/}
              {/*      </Select>*/}
              {/*    </FormItem>*/}
              {/*  </Col>*/}
              {/*)}*/}
              {/*{!(isS5Form || isS5GTForm || isJaecooForm) && !!currentBenefit && (*/}
              {/*  <Col span={24}>*/}
              {/*    <Typography.Text className={style.benefit}>*/}
              {/*      {currentBenefit.getBenefitText(car)}*/}
              {/*    </Typography.Text>*/}
              {/*  </Col>*/}
              {/*)}*/}
              <FormItem name={PreOrderFormFields.benefit} hidden>
                <Input />
              </FormItem>

              {isOmodaForm && (
                <Col span={12}>
                  {promoVisible ? (
                    <FormItem
                      label="Промокод"
                      name={PreOrderFormFields.promo}
                      rules={[FORM_ITEM_RULES.whitespace, FORM_ITEM_RULES.maxLength(15)]}
                    >
                      <Input />
                    </FormItem>
                  ) : (
                    <Button
                      type="ghost"
                      className={style.submitBtn}
                      onClick={showPromoInput}
                    >
                      Есть промокод
                    </Button>
                  )}
                </Col>
              )}
              <Col span={24}>
                <FormItem
                  name={PreOrderFormFields.confirm_personal_data_processing_policy}
                  rules={[FORM_ITEM_RULES.required, CUSTOM_RULES.personalData]}
                  valuePropName="checked"
                  className={style.checkboxField}
                >
                  <Checkbox>
                    Даю согласие на обработку{' '}
                    <Typography.Link
                      href={POLICY_PERSONAL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={style.personalDataLink}
                    >
                      персональных данных
                    </Typography.Link>
                  </Checkbox>
                </FormItem>
              </Col>
            </Row>
            <FormItem hidden name={PreOrderFormFields.captcha_token}>
              <Captcha sitekey={config.get('captchaSitekey')} visible={captchaVisible} />
            </FormItem>
            {showErrorMessage && (
              <Typography.Paragraph className={style.errorMessage}>
                {status.message}
              </Typography.Paragraph>
            )}
            <div className={style.footer}>
              <Button
                type="ghost"
                htmlType="submit"
                disabled={isSubmitting}
                className={style.submitBtn}
              >
                Продолжить оформление
              </Button>
              {!isJaecooForm && (
                <Typography.Text className={style.explainRequired}>
                  Все поля обязательны для заполнения
                </Typography.Text>
              )}
            </div>
          </>
        );
      }}
    </Form>
  );
};

export default PreOrderForm;
